/**
 * Error predicates against the `ApiClientError` shape (lib/api-client.ts).
 *
 * Reads `.status` structurally rather than importing the class, so these
 * stay decoupled from the client and usable on any thrown value. B1.1
 * will add `useApiErrorBoundary` here for the 401 → logout flow.
 */

/** HTTP status code off a thrown error, if present. */
export function statusOf(err: unknown): number | undefined {
  if (typeof err === "object" && err !== null && "status" in err) {
    const s = (err as { status?: unknown }).status;
    return typeof s === "number" ? s : undefined;
  }
  return undefined;
}

export function isUnauthorized(err: unknown): boolean {
  return statusOf(err) === 401;
}

/**
 * The backend's error CODE off a thrown error — the `error` field of the
 * standard envelope `{ error, message }`, carried on `ApiError.body`.
 *
 * Status alone is not enough to explain a failure. One status routinely covers
 * unrelated causes: a 403 on the twin surface can mean "your org lacks the
 * pack", "your role can't do this", or "this is a REAL plant and we refuse to
 * fabricate data on it". Telling a customer the wrong one sends them hunting
 * for a permissions problem that does not exist. Callers should branch on the
 * code and fall back to status only when there is none.
 */
export function errorCodeOf(err: unknown): string | undefined {
  if (typeof err !== "object" || err === null || !("body" in err)) return undefined;
  const body = (err as { body?: unknown }).body;
  if (typeof body !== "object" || body === null || !("error" in body)) return undefined;
  const code = (body as { error?: unknown }).error;
  return typeof code === "string" ? code : undefined;
}

/**
 * The backend's `detail` — the second field of the standard error envelope
 * `{ error, detail }`, carried on `ApiError.body`.
 *
 * S5.4: some refusals are only useful if their SENTENCE reaches the operator. A
 * 409 `tag_name_collides` says which piece of equipment already holds the
 * datapoint name and what to do about it; the code alone ("tag_name_collides")
 * is a label, not an instruction. Nothing read this field before, so every such
 * sentence the API produced was written and then dropped at the client boundary —
 * the same dead wire this build exists to catch, one layer out.
 */
export function errorDetailOf(err: unknown): string | undefined {
  if (typeof err !== "object" || err === null || !("body" in err)) return undefined;
  const body = (err as { body?: unknown }).body;
  if (typeof body !== "object" || body === null || !("detail" in body)) return undefined;
  const d = (body as { detail?: unknown }).detail;
  return typeof d === "string" && d.trim() !== "" ? d : undefined;
}

/**
 * Whether the product gate refused the request (M1b; plan of record docs/plans/2026-10-13_microvision-and-the-account-layer.md,
 * section A): the org does not hold the product (403 product_not_enabled), or holds it suspended or past its trial (403
 * product_suspended). A retry changes nothing; a panel the org's product does not cover hides as it does for a vendor
 * overlay.
 */
export function isProductRefused(err: unknown): boolean {
  const code = errorCodeOf(err);
  return code === "product_not_enabled" || code === "product_suspended";
}

/** One offending request field of a 400/422 `FieldProblems` body. */
export interface FieldProblem {
  /** Where in the request, e.g. `link.params.host` or `params[1].address.register`. */
  path: string;
  message: string;
}

/**
 * The `fields[]` of a 400/422 `FieldProblems` body (instrument create / patch,
 * browse), or `[]` when the error carries none. Entries that are not
 * `{path, message}` strings are dropped.
 */
export function fieldProblemsOf(err: unknown): FieldProblem[] {
  if (typeof err !== "object" || err === null || !("body" in err)) return [];
  const body = (err as { body?: unknown }).body;
  if (typeof body !== "object" || body === null || !("fields" in body)) return [];
  const fields = (body as { fields?: unknown }).fields;
  if (!Array.isArray(fields)) return [];
  return fields.flatMap((f): FieldProblem[] => {
    if (typeof f !== "object" || f === null) return [];
    const { path, message } = f as { path?: unknown; message?: unknown };
    return typeof path === "string" && typeof message === "string" ? [{ path, message }] : [];
  });
}

/**
 * A request's field problems sorted onto the form that sent it:
 * - `link`: the connection form's fields, keyed by param name
 *   (`link.params.host` → `host`; `link.driver` → `driver`);
 * - `points`: per point index, keyed by address field
 *   (`params[1].address.register` → points[1].register) or by the point's own
 *   field (`params[0].data_type` → points[0].data_type; a bare `params[2]` →
 *   points[2][""]);
 * - `other`: everything else, as "path: message" lines to show above the form.
 */
export interface FormFieldErrors {
  link: Record<string, string>;
  points: Record<number, Record<string, string>>;
  other: string[];
}

export function toFormFieldErrors(problems: FieldProblem[]): FormFieldErrors {
  const out: FormFieldErrors = { link: {}, points: {}, other: [] };
  for (const { path, message } of problems) {
    const link = /^link\.params\.([^.[\]]+)/.exec(path) ?? /^link\.(driver)$/.exec(path);
    if (link) {
      out.link[link[1]] ??= message;
      continue;
    }
    const point = /^params\[(\d+)\](?:\.address)?(?:\.([^.[\]]+))?/.exec(path);
    if (point) {
      const i = Number(point[1]);
      const field = point[2] ?? "";
      out.points[i] ??= {};
      out.points[i][field] ??= message;
      continue;
    }
    out.other.push(path ? `${path}: ${message}` : message);
  }
  return out;
}

/**
 * Toast text for a failed WRITE.
 *
 * `backendErrorMessage` is for a failed READ and says so — it names the fallback
 * being shown instead ("showing local data"). Using it on a write is actively
 * misleading, and it was: a 409 from the manual-tag form rendered as *"Couldn't
 * load from the backend (HTTP 409) — showing local data"* on an action the
 * operator had just taken, with the server's explanation of what actually went
 * wrong discarded. And because that string is non-empty, the caller's `||`
 * fallback never fired, so the generic message could not even take over.
 *
 * The rule here is the same one the backend follows: prefer what the server
 * SAID over what the client can guess. A `detail` wins; otherwise the caller's
 * own sentence; otherwise the status, plainly labelled as a write failure.
 */
export function writeErrorMessage(err: unknown, fallback: string): string {
  if (isUnauthorized(err)) return "Session expired. Please sign in again.";
  const detail = errorDetailOf(err);
  if (detail) return detail;
  if (isNetworkError(err)) return `${fallback} Backend unreachable.`;
  const s = statusOf(err);
  return s === undefined ? fallback : `${fallback} (HTTP ${s})`;
}

/**
 * A network-level failure (DNS, connection refused, CORS) rather than an
 * HTTP error response. `fetch` rejects with a TypeError; ApiClientError
 * always carries a numeric status, so "no status" ⇒ network.
 */
export function isNetworkError(err: unknown): boolean {
  if (err instanceof TypeError) return true;
  return statusOf(err) === undefined;
}

/**
 * User-facing toast text for a read-fallback failure. Distinguishes the
 * causes the data stores otherwise all blamed on the connection: an
 * expired/absent session (401), a missing org scope or access (403), and a
 * genuinely unreachable backend (network). `fallback` names what's shown
 * instead (e.g. "local data", "local plant model").
 */
export function backendErrorMessage(err: unknown, fallback = "local data"): string {
  if (isUnauthorized(err)) {
    return "Session expired. Please sign in again.";
  }
  if (statusOf(err) === 403) {
    return `No access to this org — showing ${fallback}.`;
  }
  if (isNetworkError(err)) {
    return `Backend unreachable — showing ${fallback}. Check VITE_API_BASE_URL.`;
  }
  return `Couldn't load from the backend (HTTP ${statusOf(err)}) — showing ${fallback}.`;
}
